<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import { input } from '../utils/minhaLaisOptions';
import {
  crmStatusClass,
  crmStatusLabel,
  formatDateTime,
} from '../utils/minhaLaisPresenters';

defineProps({
  crmProviderOptions: { type: Array, default: () => [] },
  crmHelp: { type: Object, default: () => ({}) },
  isLoadingCrm: { type: Boolean, default: false },
  isSavingCrm: { type: Boolean, default: false },
  isTestingCrm: { type: Boolean, default: false },
  isActivatingCrm: { type: Boolean, default: false },
  canUpdateSettings: { type: Boolean, default: false },
});

const emit = defineEmits(['save', 'test', 'activate']);

const crmConfig = defineModel({
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Configuração do CRM">
    <div
      v-if="isLoadingCrm"
      class="flex items-center gap-2 rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4 text-[13px] text-[#6b7280]"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      Carregando configuração de CRM...
    </div>

    <template v-else>
      <div
        class="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
      >
        <div>
          <p class="text-[13px] text-lais-text-muted">
            Escolha o CRM da imobiliária, salve a URL de envio de leads e teste
            a integração antes de liberar o envio automático.
          </p>
          <p
            v-if="crmConfig.activated_at"
            class="mt-2 text-[12px] text-[#6b7280]"
          >
            Ativado em {{ formatDateTime(crmConfig.activated_at) }}
          </p>
        </div>
        <span
          class="inline-flex w-fit items-center rounded-full border px-3 py-1 text-[12px] font-semibold"
          :class="crmStatusClass(crmConfig.status)"
        >
          {{ crmStatusLabel(crmConfig.status) }}
        </span>
      </div>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LaisField label="Sistema de CRM">
          <LaisSelect
            v-model="crmConfig.provider"
            :options="crmProviderOptions"
            placeholder="Escolha o CRM"
          />
        </LaisField>

        <LaisField v-if="crmConfig.provider === 'other'" label="Nome do CRM">
          <input
            v-model="crmConfig.provider_label"
            type="text"
            :class="input"
            placeholder="Ex.: CRM próprio"
          />
        </LaisField>

        <LaisField
          label="URL de envio de leads"
          helper="Endpoint HTTP/HTTPS informado pelo CRM"
          class="lg:col-span-2"
        >
          <input
            v-model="crmConfig.lead_endpoint_url"
            type="url"
            :class="input"
            placeholder="https://api.seucrm.com.br/leads"
          />
        </LaisField>

        <LaisField
          label="Token/chave de integração"
          :helper="
            crmConfig.token_present
              ? 'Token salvo. Preencha apenas se quiser trocar.'
              : 'Obrigatório para testar a integração.'
          "
        >
          <input
            v-model="crmConfig.token"
            type="password"
            autocomplete="new-password"
            :class="input"
            placeholder="Cole a chave de API"
          />
        </LaisField>

        <LaisField
          label="Nome do header/token"
          helper="Use Authorization quando o CRM não pedir um header próprio"
        >
          <input
            v-model="crmConfig.auth_header_name"
            type="text"
            :class="input"
          />
        </LaisField>
      </div>

      <div class="mt-5 grid gap-3 sm:grid-cols-3">
        <label
          class="flex cursor-pointer items-start gap-3 rounded-lais border border-[#e5e7eb] bg-white p-3"
        >
          <input
            v-model="crmConfig.include_conversation"
            type="checkbox"
            class="mt-1 size-4 accent-lais-primary"
          />
          <span>
            <span class="block text-[13px] font-semibold text-lais-text">
              Enviar conversa completa
            </span>
            <span class="block text-[12px] text-[#6b7280]">
              Inclui resumo/transcrição recente.
            </span>
          </span>
        </label>

        <label
          class="flex cursor-pointer items-start gap-3 rounded-lais border border-[#e5e7eb] bg-white p-3"
        >
          <input
            v-model="crmConfig.include_property"
            type="checkbox"
            class="mt-1 size-4 accent-lais-primary"
          />
          <span>
            <span class="block text-[13px] font-semibold text-lais-text">
              Enviar imóvel de interesse
            </span>
            <span class="block text-[12px] text-[#6b7280]">
              Leva código e operação quando houver.
            </span>
          </span>
        </label>

        <label
          class="flex cursor-pointer items-start gap-3 rounded-lais border border-[#e5e7eb] bg-white p-3"
        >
          <input
            v-model="crmConfig.include_origin"
            type="checkbox"
            class="mt-1 size-4 accent-lais-primary"
          />
          <span>
            <span class="block text-[13px] font-semibold text-lais-text">
              Enviar origem/campanha
            </span>
            <span class="block text-[12px] text-[#6b7280]">
              Preserva portal, mídia e campanha.
            </span>
          </span>
        </label>
      </div>
    </template>
  </LaisCard>

  <LaisCard title="Teste e prontidão">
    <div class="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
      <div class="rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4">
        <p class="text-[13px] font-semibold text-lais-text">
          {{ crmHelp.title || 'Onde encontro URL e token?' }}
        </p>
        <p class="mt-1 text-[13px] leading-relaxed text-[#6b7280]">
          {{
            crmHelp.body ||
            'Use a URL de entrada de leads e a chave de integração fornecidas pelo CRM.'
          }}
        </p>
        <p
          v-if="crmConfig.last_test_error"
          class="mt-3 rounded-lais-sm bg-[#fff5f6] px-3 py-2 text-[12px] font-semibold text-[#b42318]"
        >
          {{ crmConfig.last_test_error }}
        </p>
        <p
          v-else-if="crmConfig.last_test_status === 'success'"
          class="mt-3 rounded-lais-sm bg-[#edfdf5] px-3 py-2 text-[12px] font-semibold text-[#067647]"
        >
          Último teste concluído em
          {{ formatDateTime(crmConfig.last_tested_at) }}.
        </p>
      </div>

      <div class="flex flex-wrap gap-2 lg:justify-end">
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais border border-[#e5e7eb] bg-white px-4 text-[13px] font-semibold text-lais-primary transition-colors hover:border-lais-primary/50 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isSavingCrm || !canUpdateSettings"
          @click="emit('save')"
        >
          <span
            class="size-4"
            :class="
              isSavingCrm
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-save'
            "
          />
          Salvar CRM
        </button>
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais border border-[#e5e7eb] bg-white px-4 text-[13px] font-semibold text-lais-primary transition-colors hover:border-lais-primary/50 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isTestingCrm || !canUpdateSettings"
          @click="emit('test')"
        >
          <span
            class="size-4"
            :class="
              isTestingCrm
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-plug-zap'
            "
          />
          Testar integração
        </button>
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#3a2762] disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="
            isActivatingCrm ||
            !canUpdateSettings ||
            crmConfig.last_test_status !== 'success'
          "
          @click="emit('activate')"
        >
          <span
            class="size-4"
            :class="
              isActivatingCrm
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-check-circle'
            "
          />
          Marcar CRM pronto
        </button>
      </div>
    </div>
  </LaisCard>
</template>
